/**
 * Item composer (new + edit) — V1.0.x catalogue editor (ADR-0013).
 *
 * Mirrors `prototype/catalogue/item-new.html`:
 *   • Sticky composer-head with back + status chip.
 *   • 7 sections with progressive disclosure:
 *       1 Identity (always open) · 2 Categorisation · 3 Pricing & GST
 *       (3 above-the-fold)
 *       4 Stock & inventory · 5 Vendor link · 6 Compliance · 7 Notes
 *       (4-7 collapsed by default)
 *   • Sticky composer-foot with split-button save.
 *
 * `loadItemId` prop drives edit mode (the route shim sets it from the
 * URL param). Same component handles new + edit so the field surface
 * stays in one place — anti-pattern The Copy-Paste Helper, AGENTS.md §7.
 *
 * @see prototype/catalogue/item-new.html
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown, Package, Plus, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import {
  EMPTY_FORM,
  formFromItem,
  type ItemFormValues,
  valuesToCreatePayload,
  valuesToUpdatePayload,
} from "../lib/form-defaults.ts";
import "./item-composer-page.css";

const UNITS = ["Nos", "pcs", "kg", "g", "m", "cm", "sqft", "sqm", "L", "mL", "hr", "day", "can", "pack", "drum", "box", "set", "lot", "dozen", "bundle", "roll", "sheet"];
const GST_OPTIONS = [
  { label: "0%", value: "0" },
  { label: "0.25%", value: "25" },
  { label: "3%", value: "300" },
  { label: "5%", value: "500" },
  { label: "12%", value: "1200" },
  { label: "18%", value: "1800" },
  { label: "28%", value: "2800" },
  { label: "40%", value: "4000" },
];
const CATEGORY_PRESETS = [
  "Fasteners",
  "Sheet metal",
  "Consumables",
  "Packaging",
  "Job work",
  "Finishing",
];
const TDS_SECTIONS = ["", "194Q", "194C", "194J"];

export interface ItemComposerPageProps {
  loadItemId?: string;
  /** Item id to seed a duplicate from — hydrates values with SKU cleared. */
  duplicateFromId?: string;
}

export function ItemComposerPage({ loadItemId, duplicateFromId }: ItemComposerPageProps) {
  const navigate = useNavigate();
  const isEdit = Boolean(loadItemId);
  const isDuplicate = !isEdit && Boolean(duplicateFromId);
  const sourceItemId = loadItemId ?? duplicateFromId ?? "";

  const loadedItem = trpc.items.get.useQuery(
    { id: sourceItemId },
    { enabled: isEdit || isDuplicate },
  );
  const vendors = trpc.vendors.list.useQuery({ limit: 100 });
  const utils = trpc.useUtils();

  const [values, setValues] = useState<ItemFormValues>(EMPTY_FORM);
  const [baseline, setBaseline] = useState<ItemFormValues>(EMPTY_FORM);
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({
    4: true,
    5: true,
    6: true,
    7: true,
  });
  const [splitOpen, setSplitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate on edit-mode load.
  useEffect(() => {
    if (!isEdit || !loadedItem.data) return;
    const next = formFromItem(loadedItem.data);
    setValues(next);
    setBaseline(next);
  }, [isEdit, loadedItem.data]);

  // Hydrate on duplicate-from load — clear SKU (unique) and mark name as a copy.
  // Baseline stays EMPTY_FORM so every field is treated as new on save.
  useEffect(() => {
    if (!isDuplicate || !loadedItem.data) return;
    const src = formFromItem(loadedItem.data);
    setValues({ ...src, sku: "", name: `${src.name} (copy)` });
  }, [isDuplicate, loadedItem.data]);

  const create = trpc.items.create.useMutation();
  const update = trpc.items.update.useMutation();

  const onPatch = <K extends keyof ItemFormValues>(key: K, value: ItemFormValues[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
  };

  const submit = async (afterSave: "view" | "duplicate" | "add-another" | "close") => {
    setError(null);
    setSubmitting(true);
    try {
      let savedId: string;
      if (isEdit && loadItemId) {
        const patch = valuesToUpdatePayload(values, baseline, loadItemId);
        // Only fire the mutation if there's at least one changed key.
        const keys = Object.keys(patch).filter((k) => k !== "id");
        if (keys.length > 0) {
          await update.mutateAsync(patch);
        }
        savedId = loadItemId;
      } else {
        const payload = valuesToCreatePayload(values);
        const result = await create.mutateAsync(payload);
        savedId = result.id;
      }
      await utils.items.list.invalidate();
      await utils.items.counts.invalidate();
      await utils.items.get.invalidate({ id: savedId });
      if (afterSave === "view") {
        void navigate({ to: "/sales/items/$itemId", params: { itemId: savedId } });
      } else if (afterSave === "duplicate") {
        setValues({ ...values, name: `${values.name} (copy)`, sku: "" });
        setBaseline(EMPTY_FORM);
      } else if (afterSave === "add-another") {
        setValues(EMPTY_FORM);
        setBaseline(EMPTY_FORM);
      } else {
        void navigate({ to: "/sales/items" });
      }
    } catch (err) {
      setError(describeSaveError(err));
    } finally {
      setSubmitting(false);
      setSplitOpen(false);
    }
  };

  const sectionToggle = (id: number) => () => {
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  };

  const onAddTag = (raw: string) => {
    const tag = raw.trim().toLowerCase();
    if (!tag) return;
    if (values.tags.includes(tag)) return;
    onPatch("tags", [...values.tags, tag]);
  };
  const onRemoveTag = (tag: string) => {
    onPatch(
      "tags",
      values.tags.filter((t) => t !== tag),
    );
  };

  const vendorOptions = useMemo(() => vendors.data?.items ?? [], [vendors.data]);

  return (
    <div className="item-composer-page">
      {/* ── Header — pinned ──────────────────────────────────────── */}
      <header className="ic-head">
        <Link to="/sales/items" className="ic-head-back" aria-label="Back to items">
          <ArrowLeft size={16} strokeWidth={2.2} />
        </Link>
        <div className="ic-head-icon" aria-hidden>
          <Package size={16} />
        </div>
        <div className="ic-head-text">
          <h1>{isEdit ? "Edit item" : "New item"}</h1>
        </div>
        <span className="ic-status-chip">{isEdit ? "Saved" : "Draft"}</span>
      </header>

      {/* ── Scrollable body ───────────────────────────────────────── */}
      <form
        id="ic-form"
        className="ic-body"
        onSubmit={(e) => {
          e.preventDefault();
          void submit("view");
        }}
      >
        <div className="ic-body-inner">
        {error ? (
          <div className="ic-error" role="alert">
            <p>{error}</p>
          </div>
        ) : null}

        {/* ── 1 · Identity (always open) ───────────────────── */}
        <Section
          step={1}
          title="Identity"
          why="Name + SKU + type. The fields the picker uses to find this item."
          collapsed={collapsed[1] ?? false}
          onToggle={sectionToggle(1)}
        >
          <div className="grid-2">
            <div className="field full">
              <label>
                Display name <span className="req">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. M8 Hex Bolt — 50mm"
                value={values.name}
                onChange={(e) => onPatch("name", e.target.value)}
                required
                autoFocus
              />
              <span className="help">
                Shown in the line typeahead + printed on invoices and bills.
              </span>
            </div>

            <div className="field">
              <label>
                SKU <span className="opt">optional</span>
              </label>
              <input
                type="text"
                placeholder="e.g. FAST-M8-50"
                value={values.sku}
                onChange={(e) => onPatch("sku", e.target.value)}
              />
              <span className="help">Unique within active items. Archived SKUs can be reused.</span>
            </div>

            <div className="field">
              <label>Type</label>
              <div className="radio-row">
                <input
                  type="radio"
                  name="ic-type"
                  id="ic-goods"
                  checked={values.itemType === "goods"}
                  onChange={() => onPatch("itemType", "goods")}
                />
                <label htmlFor="ic-goods">Goods</label>
                <input
                  type="radio"
                  name="ic-type"
                  id="ic-service"
                  checked={values.itemType === "service"}
                  onChange={() => onPatch("itemType", "service")}
                />
                <label htmlFor="ic-service">Service</label>
                <input type="radio" name="ic-type" id="ic-bundle" disabled />
                <label htmlFor="ic-bundle" className="is-disabled">
                  Bundle <span className="future-chip">V1.x</span>
                </label>
              </div>
            </div>

            <div className="field full">
              <label>
                Description <span className="opt">optional</span>
              </label>
              <textarea
                placeholder="Long-form line shown on the printed document. Up to 500 chars."
                value={values.description}
                maxLength={500}
                onChange={(e) => onPatch("description", e.target.value)}
              />
            </div>

            <div className="field">
              <label>Usage</label>
              <div className="radio-row">
                <input
                  type="radio"
                  name="ic-usage"
                  id="ic-sale"
                  checked={values.usage === "sale"}
                  onChange={() => onPatch("usage", "sale")}
                />
                <label htmlFor="ic-sale">Sale</label>
                <input
                  type="radio"
                  name="ic-usage"
                  id="ic-purchase"
                  checked={values.usage === "purchase"}
                  onChange={() => onPatch("usage", "purchase")}
                />
                <label htmlFor="ic-purchase">Purchase</label>
                <input
                  type="radio"
                  name="ic-usage"
                  id="ic-both"
                  checked={values.usage === "both"}
                  onChange={() => onPatch("usage", "both")}
                />
                <label htmlFor="ic-both">Both</label>
              </div>
              <span className="help">Decides which composer's picker surfaces this item.</span>
            </div>
          </div>
        </Section>

        {/* ── 2 · Categorisation ───────────────────────────── */}
        <Section
          step={2}
          title="Categorisation"
          why="Category, brand, tags — filters and segmentation on the list page."
          collapsed={collapsed[2] ?? false}
          onToggle={sectionToggle(2)}
        >
          <div className="grid-2">
            <div className="field">
              <label>Category</label>
              <input
                type="text"
                list="ic-cat-presets"
                placeholder="e.g. Fasteners"
                value={values.category}
                onChange={(e) => onPatch("category", e.target.value)}
              />
              <datalist id="ic-cat-presets">
                {CATEGORY_PRESETS.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className="field">
              <label>Brand</label>
              <input
                type="text"
                placeholder="e.g. TVS Srichakra"
                value={values.brand}
                onChange={(e) => onPatch("brand", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Manufacturer</label>
              <input
                type="text"
                placeholder="e.g. TVS Industrial"
                value={values.manufacturer}
                onChange={(e) => onPatch("manufacturer", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Barcode</label>
              <input
                type="text"
                placeholder="EAN / UPC / Code-128"
                value={values.barcode}
                onChange={(e) => onPatch("barcode", e.target.value)}
              />
            </div>
            <div className="field full">
              <label>Tags</label>
              <div className="tags-input">
                {values.tags.map((t) => (
                  <span key={t} className="tag-chip">
                    {t}
                    <span
                      className="x"
                      onClick={() => onRemoveTag(t)}
                      role="button"
                      aria-label={`Remove ${t}`}
                    >
                      ×
                    </span>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder="Press enter to add…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onAddTag((e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = "";
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </Section>

        {/* ── 3 · Pricing & GST ────────────────────────────── */}
        <Section
          step={3}
          title="Pricing & GST"
          why="Default rate the composer pre-fills + GST treatment."
          collapsed={collapsed[3] ?? false}
          onToggle={sectionToggle(3)}
        >
          <div className="grid-3">
            <div className="field">
              <label>Default rate (₹)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="15.00"
                value={values.defaultRateRupees}
                onChange={(e) => onPatch("defaultRateRupees", e.target.value)}
              />
              <span className="help">Per default unit. Snapshotted at issue.</span>
            </div>
            <div className="field">
              <label>Default unit</label>
              <input
                type="text"
                list="ic-unit-presets"
                placeholder="e.g. Nos, kg, m…"
                value={values.unit}
                onChange={(e) => onPatch("unit", e.target.value)}
              />
              <datalist id="ic-unit-presets">
                {UNITS.map((u) => (
                  <option key={u} value={u} />
                ))}
              </datalist>
              <span className="help">Pick a preset or type a custom unit.</span>
            </div>
            <div className="field">
              <label>HSN/SAC</label>
              <input
                type="text"
                placeholder="7318"
                maxLength={8}
                value={values.hsn}
                onChange={(e) => onPatch("hsn", e.target.value.replace(/\D/g, "").slice(0, 8))}
              />
            </div>
            <div className="field">
              <label>Tax preference</label>
              <Select
                value={values.taxPreference}
                onValueChange={(v) => {
                  const next = v as ItemFormValues["taxPreference"];
                  // Non-taxable preferences don't carry a slab. Clear the
                  // retained bps so a stale value from an earlier "Taxable"
                  // selection can't leak into the payload.
                  setValues((prev) => ({
                    ...prev,
                    taxPreference: next,
                    ...(next !== "taxable" ? { defaultTaxRateBpsStr: "" } : {}),
                  }));
                }}
              >
                <SelectTrigger className="!h-9 !text-sm" aria-label="Tax preference">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="taxable">Taxable</SelectItem>
                  <SelectItem value="exempt">Exempt</SelectItem>
                  <SelectItem value="nil_rated">Nil-rated</SelectItem>
                  <SelectItem value="non_gst">Non-GST</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <label>GST slab</label>
              <Select
                value={values.defaultTaxRateBpsStr || "__none__"}
                disabled={values.taxPreference !== "taxable"}
                onValueChange={(v) => onPatch("defaultTaxRateBpsStr", v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="!h-9 !text-sm" aria-label="GST slab">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {GST_OPTIONS.map((g) => (
                    <SelectItem key={g.value} value={g.value}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <label>
                Cess (bps) <span className="opt">optional</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="0"
                value={values.cessRateBpsStr}
                onChange={(e) => onPatch("cessRateBpsStr", e.target.value.replace(/\D/g, ""))}
              />
            </div>
            <div className="field">
              <label>
                Last cost (₹) <span className="opt">optional</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="11.80"
                value={values.lastCostRupees}
                onChange={(e) => onPatch("lastCostRupees", e.target.value)}
              />
              <span className="help">Used to compute margin pill on list.</span>
            </div>
            <div className="field">
              <label>
                MRP (₹) <span className="opt">optional</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="—"
                value={values.mrpRupees}
                onChange={(e) => onPatch("mrpRupees", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Default TDS section</label>
              <Select
                value={values.tdsSectionDefault || "__none__"}
                onValueChange={(v) => onPatch("tdsSectionDefault", v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="!h-9 !text-sm" aria-label="Default TDS section">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TDS_SECTIONS.map((s) => (
                    <SelectItem key={s || "none"} value={s || "__none__"}>
                      {s || "none"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Section>

        {/* ── 4 · Stock & inventory (collapsed) ───────────── */}
        <Section
          step={4}
          title="Stock & inventory"
          why="Reorder point, track-stock flag, alt units. V1.0.x captures the flags; balances arrive with the stock module."
          collapsed={collapsed[4] ?? true}
          onToggle={sectionToggle(4)}
        >
          <div className="stock-grid">
            <div className="field">
              <label>Track stock</label>
              <div className="radio-row">
                <input
                  type="radio"
                  name="ic-track"
                  id="ic-tr-no"
                  checked={!values.trackStock}
                  onChange={() => onPatch("trackStock", false)}
                />
                <label htmlFor="ic-tr-no">Off</label>
                <input
                  type="radio"
                  name="ic-track"
                  id="ic-tr-yes"
                  checked={values.trackStock}
                  onChange={() => onPatch("trackStock", true)}
                />
                <label htmlFor="ic-tr-yes">On</label>
              </div>
            </div>
            <div className="field">
              <label>Reorder level</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="200"
                value={values.reorderLevel}
                onChange={(e) => onPatch("reorderLevel", e.target.value.replace(/\D/g, ""))}
              />
            </div>
            <div className="field">
              <label>
                Weight (g) <span className="opt">optional</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="—"
                value={values.weightGrams}
                onChange={(e) => onPatch("weightGrams", e.target.value.replace(/\D/g, ""))}
              />
            </div>
          </div>

          {/* Alt units — bordered table, mirrors PO line-items design */}
          <div className="alt-units-block">
            <span className="alt-units-label">Alt units</span>
            <div className="alt-units-table-wrap">
              <table className="alt-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Factor (× base)</th>
                    <th aria-label="Remove" />
                  </tr>
                </thead>
                <tbody>
                  {values.altUnits.map((a, i) => (
                    <tr key={i}>
                      <td className="alt-td-unit">
                        <input
                          list="ic-unit-presets"
                          placeholder="e.g. dozen"
                          value={a.unit}
                          onChange={(e) => {
                            const next = [...values.altUnits];
                            next[i] = { ...a, unit: e.target.value };
                            onPatch("altUnits", next);
                          }}
                        />
                      </td>
                      <td className="alt-td-factor">
                        <input
                          inputMode="decimal"
                          value={String(a.factor)}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            const next = [...values.altUnits];
                            next[i] = { ...a, factor: Number.isFinite(n) ? n : 0 };
                            onPatch("altUnits", next);
                          }}
                        />
                      </td>
                      <td className="alt-td-remove">
                        <button
                          type="button"
                          aria-label="Remove"
                          className="alt-remove-btn"
                          onClick={() =>
                            onPatch(
                              "altUnits",
                              values.altUnits.filter((_, j) => j !== i),
                            )
                          }
                        >
                          <X size={13} aria-hidden />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="alt-add-row">
                <button
                  type="button"
                  className="alt-add"
                  onClick={() => onPatch("altUnits", [...values.altUnits, { unit: "", factor: 1 }])}
                >
                  + Add alt unit
                </button>
              </div>
            </div>
          </div>
        </Section>

        {/* ── 5 · Vendor link (collapsed) ─────────────────── */}
        <Section
          step={5}
          title="Vendor link"
          why="Preferred vendor + lead time + MOQ. Pre-fills bill composer."
          collapsed={collapsed[5] ?? true}
          onToggle={sectionToggle(5)}
        >
          <div className="grid-3">
            <div className="field full">
              <label>Preferred vendor</label>
              <Select
                value={values.preferredVendorId || "__none__"}
                onValueChange={(v) => onPatch("preferredVendorId", v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="!h-9 !text-sm" aria-label="Preferred vendor">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {vendorOptions.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <label>Lead time (days)</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="5"
                value={values.leadTimeDays}
                onChange={(e) => onPatch("leadTimeDays", e.target.value.replace(/\D/g, ""))}
              />
            </div>
            <div className="field">
              <label>MOQ</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="50"
                value={values.minOrderQty}
                onChange={(e) => onPatch("minOrderQty", e.target.value.replace(/\D/g, ""))}
              />
            </div>
            <div className="field">
              <label>Vendor SKU</label>
              <input
                type="text"
                placeholder="vendor's catalogue code"
                value={values.vendorSku}
                onChange={(e) => onPatch("vendorSku", e.target.value)}
              />
            </div>
          </div>
        </Section>

        {/* ── 6 · Compliance (collapsed) ──────────────────── */}
        <Section
          step={6}
          title="Compliance & metadata"
          why="e-invoice / e-way bill defaults. V1.0.x captures the columns; outbound wiring arrives with compliance module."
          collapsed={collapsed[6] ?? true}
          onToggle={sectionToggle(6)}
        >
          <div className="grid-2">
            <div className="field">
              <label>e-invoice eligible</label>
              <Select disabled value="auto_turnover">
                <SelectTrigger className="!h-9 !text-sm" aria-label="e-invoice eligible">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto_turnover">Auto (V1.x — based on turnover)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <label>e-way bill default</label>
              <Select disabled value="auto_value">
                <SelectTrigger className="!h-9 !text-sm" aria-label="e-way bill default">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto_value">Auto (V1.x — based on value)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Section>

        {/* ── 7 · Notes (collapsed) ───────────────────────── */}
        <Section
          step={7}
          title="Notes"
          why="Internal note — not printed on any document."
          collapsed={collapsed[7] ?? true}
          onToggle={sectionToggle(7)}
        >
          <div className="field full">
            <label>
              Note <span className="opt">optional</span>
            </label>
            <textarea placeholder="Anything you want the team to remember about this item." />
            <span className="help">
              V1.0.x captures the field on the form; persistence lands with the audit-log search
              rollout.
            </span>
          </div>
        </Section>

        </div>
      </form>

      {/* ── Footer — pinned ──────────────────────────────────────── */}
      <footer className="ic-foot">
        <div className="ic-foot-status">
          <strong>Required:</strong> name only. Everything else can be filled later.
        </div>
        <div className="ic-foot-actions">
          <button
            type="button"
            className="inline-flex items-center h-9 px-4 rounded-md border border-line bg-paper text-sm font-semibold text-ink-2 hover:bg-bg"
            onClick={() => void navigate({ to: "/sales/items" })}
          >
            Cancel
          </button>
          <div className="split">
            <button
              className="primary"
              type="submit"
              form="ic-form"
              disabled={submitting || !values.name.trim()}
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Save item"}
            </button>
            <button
              type="button"
              className="caret"
              aria-label="More save options"
              onClick={() => setSplitOpen((s) => !s)}
            >
              <ChevronDown size={12} aria-hidden />
            </button>
            <div className={`split-menu ${splitOpen ? "show" : ""}`} role="menu">
              <button type="button" onClick={() => void submit("view")}>
                Save &amp; view
              </button>
              <button type="button" onClick={() => void submit("duplicate")}>
                Save &amp; duplicate
              </button>
              <button type="button" onClick={() => void submit("add-another")}>
                Save &amp; add another
              </button>
              <button type="button" onClick={() => void submit("close")}>
                Save &amp; close
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── Section primitive ──────────────────────────────────────────

function Section({
  step,
  title,
  why,
  collapsed,
  onToggle,
  children,
}: {
  step: number;
  title: string;
  why: string;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className={`section ${collapsed ? "is-collapsed" : ""}`}>
      <div
        className="section-head"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className="step">{step}</span>
        <div>
          <h3>{title}</h3>
          <span className="why">{why}</span>
        </div>
        <span className="toggle">
          <ChevronDown size={14} aria-hidden />
        </span>
      </div>
      <div className="section-body">{children}</div>
    </section>
  );
}

/* unused but keeps imports honest */
void Plus;

/**
 * Swap raw domain / Zod error text for readable, actionable copy on the
 * composer error banner. Mirrors `describeInvoiceMutationError` on the
 * invoices side — the raw regex Zod issue array would otherwise land in
 * the banner as `[{"validation":"regex","code":"invalid_string",…}]`.
 */
function describeSaveError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err ?? "");
  if (!message) return "Save failed. Try again.";

  // Raw Zod JSON — starts with `[` and mentions a known invalid_string / regex code.
  if (/^\[\s*\{[\s\S]*"code"\s*:\s*"(invalid_string|too_small|too_big|custom)/.test(message)) {
    if (/"path"\s*:\s*\[\s*"hsn"\s*\]/.test(message)) {
      return "HSN/SAC must be 4–8 digits with no letters or spaces.";
    }
    if (/"path"\s*:\s*\[\s*"tdsSectionDefault"\s*\]/.test(message)) {
      return "TDS section must be 3 digits followed by an optional letter (e.g. 194Q).";
    }
    if (/"path"\s*:\s*\[\s*"gstin"\s*\]/.test(message)) {
      return "GSTIN doesn't look valid. Copy-paste it directly from the certificate.";
    }
    return "Some of the entered details aren't valid. Check the highlighted rows and try again.";
  }

  if (/duplicate\s+sku|already\s+in\s+use/i.test(message)) {
    return "That SKU is already in use on another active item. Pick a different code.";
  }
  if (/bundle.*not.*supported/i.test(message)) {
    return "Bundle items aren't supported in V1. Save this as Goods or Service instead.";
  }
  if (/UNAUTHORIZED|FORBIDDEN|permission/i.test(message)) {
    return "You don't have permission to save this item.";
  }
  if (/NOT_FOUND/i.test(message)) {
    return "This item no longer exists. Refresh and try again.";
  }
  return message;
}
